import type { PartyPanelMarkerToggle, PartyPanelRow } from '@sorrem-client/ui/features/Hud';
import { useCallout } from '@sorrem-client/ui/hooks';
import type { CrucibleMember } from '@sorrem/shared';
import { useCallback, useState } from 'react';

interface ReadyToggleInput {
    /** The viewer's own party member, or null while they aren't in the party. */
    readonly self: CrucibleMember | null;
    readonly selfRowKey: string | null;
    readonly isRunLoaded: boolean;
    readonly isStarting: boolean;
    /** Marks the viewer ready (or not); resolves with null, or why the server refused. */
    readonly setReady: (isReady: boolean) => Promise<string | null>;
}

export interface ReadyToggle {
    /** Why the server last refused a ready change, or null. */
    readyError: string | null;
    /** The party panel's marker toggle: on the viewer's own row only. */
    markerToggle: (row: PartyPanelRow) => PartyPanelMarkerToggle | null;
}

/**
 * The viewer's own Ready / Not ready marker is their Ready toggle; it waits
 * for the run to load before readying. Testers read "Not ready" as a
 * status, not something to press, so it asks "Ready?", and until the account
 * first readies up a callout points at it (#237).
 */
export function useReadyToggle({ self, selfRowKey, isRunLoaded, isStarting, setReady }: ReadyToggleInput): ReadyToggle {
    const [isReadyPending, setIsReadyPending] = useState(false);
    const [readyError, setReadyError] = useState<string | null>(null);
    // The first-time "Click when ready" callout (#237); the first ready-up retires it.
    const { isOpen: isCalloutOpen, close: closeCallout, dismiss: dismissCallout } = useCallout('ready-up');

    const handleReadyChange = useCallback((isReady: boolean): void => {
        setIsReadyPending(true);
        setReadyError(null);
        void setReady(isReady).then((error) => {
            setIsReadyPending(false);
            setReadyError(error);
        });
    }, [setReady]);

    const markerToggle = useCallback((row: PartyPanelRow): PartyPanelMarkerToggle | null => {
        if (row.key !== selfRowKey) {
            return null;
        }
        const isSelfReady = self?.isReady === true;
        const disabled = isReadyPending || isStarting || (!isRunLoaded && !isSelfReady);
        return {
            onToggle: () => {
                // The first ready-up retires the callout that points at it.
                if (!isSelfReady) {
                    dismissCallout();
                }
                handleReadyChange(!isSelfReady);
            },
            ariaLabel: isSelfReady ? 'Mark yourself not ready' : 'Mark yourself ready',
            ...(isSelfReady ? {} : { label: 'Ready?' }),
            ...(isCalloutOpen && !isSelfReady && !disabled
                ? { callout: { label: `Click 'Ready?' when you're ready to join the crucible`, onClose: closeCallout, onDismiss: dismissCallout } }
                : {}),
            disabled,
        };
    }, [closeCallout, dismissCallout, handleReadyChange, isReadyPending, isRunLoaded, isStarting, self, selfRowKey, isCalloutOpen]);

    return { readyError, markerToggle };
}
